import {
  ActionBar,
  ActionBarButtons,
  CreateActionButton,
  EditActionButton,
  useApi,
  useBrowser,
  Row,
  Column,
  useNotifier,
  useTranslateError,
} from "lcm-iot-commons";
import { FormattedMessage, useIntl } from "react-intl";
import { useState } from "react";
import TechnicalUserItem from "./TechnicalUserItem";
import TechnicalUserModal from "./TechnicalUserModal";
import { technicalUserShape } from "../../shapes/technicalUsersShape";

type Props = Readonly<{
  canUpdateClientApplication?: boolean;
  clientApplicationId?: number;
  technicalUsers?: technicalUserShape[];
}>;

export default function TechnicalUsers(props: Props) {
  const api = useApi();
  const browser = useBrowser();
  const intl = useIntl();
  const notifier = useNotifier();
  const translateError = useTranslateError();

  const { canUpdateClientApplication, clientApplicationId, technicalUsers } =
    props;
  const [technicalUsersList, setTechnicalUsersList] = useState<
    technicalUserShape[]
  >(technicalUsers || []);
  const [newTechnicalUser, setNewTechnicalUser] =
    useState<technicalUserShape>();
  const [showTechnicalUserModal, setShowTechnicalUserModal] = useState(false);
  const {
    mutateAsync: createTechnicalUser,
    isLoading: isCreatingTechnicalUser,
  } = api.post.useMutation(
    `/client_applications/${clientApplicationId}/technical_users`,
    {
      onError: () => {},
    },
  );

  const renderBadge =
    technicalUsersList?.length > 0 ? (
      <span
        id="technical-user-count"
        data-testid="technical-user-count"
        className="badge"
      >
        {technicalUsersList.length}
      </span>
    ) : null;

  const handleCreateTechnicalUser = async () => {
    try {
      const newUser = await createTechnicalUser();
      setNewTechnicalUser(newUser as technicalUserShape);
      setTechnicalUsersList([
        ...technicalUsersList,
        newUser as technicalUserShape,
      ]);
      setShowTechnicalUserModal(true);
    } catch (error) {
      notifier.showError(translateError(error));
    }
  };

  const handleOnPermissionsClick = () => {
    browser.redirectTo("/nodes");
  };

  const handleOnTechnicalUserRemoved = (
    removedTechnicalUser: technicalUserShape,
  ) => {
    setTechnicalUsersList(
      technicalUsersList.filter((item) => item.id !== removedTechnicalUser.id),
    );
  };

  return (
    <>
      <Row id="subscription-technical-users" className="space-before">
        <Column>
          <ActionBar>
            <h2>
              <FormattedMessage id="subscription_details.technical_users.header" />
              {renderBadge}
            </h2>
            <ActionBarButtons>
              <CreateActionButton
                // @ts-expect-error TS(2322) FIXME: Type '{ id: string; onClick: () => Promise<void>; ... Remove this comment to see the full error message
                id="create-technical-user-button"
                onClick={handleCreateTechnicalUser}
                disabled={
                  isCreatingTechnicalUser ||
                  !canUpdateClientApplication ||
                  !clientApplicationId
                }
              />
              <EditActionButton
                id="edit-permissions-button"
                onClick={handleOnPermissionsClick}
                disabled={!canUpdateClientApplication}
                label={intl.formatMessage({ id: "button.permissions" })}
              />
            </ActionBarButtons>
          </ActionBar>
        </Column>
      </Row>
      {technicalUsersList?.length > 0 ? (
        <ul id="technical-user-item-list" className="list">
          {technicalUsersList.map((item) => (
            <TechnicalUserItem
              key={item.id}
              // @ts-expect-error TS(2322) FIXME: Type '{ key: any; technicalUser: any; onTechnicalU... Remove this comment to see the full error message
              intl={intl}
              technicalUser={item}
              onTechnicalUserRemoved={handleOnTechnicalUserRemoved}
              accessRights={{ canUpdate: canUpdateClientApplication }}
            />
          ))}
        </ul>
      ) : (
        <div id="no-technical-users-found">
          <FormattedMessage id="subscription_details.no_technical_users_assigned" />
        </div>
      )}
      {newTechnicalUser && (
        <TechnicalUserModal
          // @ts-expect-error TS(2322) FIXME: Type '{ show: any; titleText: string; ... Remove this comment to see the full error message
          show={showTechnicalUserModal}
          titleText={intl.formatMessage({
            id: "technical_user_modal.create.modal_title",
          })}
          contextBodyText={intl.formatMessage({
            id: "technical_user_modal.create.modal_message",
          })}
          onClose={() => setShowTechnicalUserModal(false)}
          username={newTechnicalUser.email}
          password={newTechnicalUser.password}
        />
      )}
    </>
  );
}
